import { useMemo } from "react";
import { convertUtcToFormattedTime } from "../../../util/AppUtils";
import ShowLightText from "../../../common/SmallButUseFull/ShowLightText";

/**
 * Custom hook to generate "Based On" text for revision
 *
 * This hook determines what the current revision is based on:
 * - If there's a based_on_version_details, it shows that information
 * - Otherwise, returns null
 *
 * @param {Object} currentRevision - The current revision object
 * @param {Array} revisionData - Optional array of all revisions to find base revision for status check
 * @returns {JSX.Element|null} The formatted "Based On" text or null
 */
const useRevisionBasedOnText = (currentRevision, revisionData = []) => {
  const basedOnText = useMemo(() => {
    if (!currentRevision) {
      return null;
    }

    // Check if there's based_on_version_details
    if (currentRevision.based_on_version_details?.version_content) {
      const versionContent =
        currentRevision.based_on_version_details.version_content;
      const rev = versionContent.rev || versionContent.revision || "";
      let current_status =
        versionContent.current_status || versionContent.status_changed_to || "";
      const procedure_number =
        versionContent.procedure_number ||
        versionContent.part_number ||
        currentRevision.part_number ||
        "";

      // Try to find the base revision from revisionData to check for pending approvals
      let baseRevision = null;
      if (revisionData && revisionData.length > 0) {
        // Try to find by revision_ref from based_on_version_details
        const revisionRef = currentRevision.based_on_version_details?.revision_ref;
        if (revisionRef) {
          baseRevision = revisionData.find(
            (r) => r.procedure_revision_id === revisionRef
          );
        }
        
        // If not found, try to find by revision number and part number
        if (!baseRevision && rev && procedure_number) {
          baseRevision = revisionData.find(
            (r) => r.rev === rev && r.part_number === procedure_number
          );
        }
      }

      // Check for pending approvals if we found the base revision
      if (baseRevision && current_status.toLowerCase() === "approved") {
        const hasPendingApprovals = baseRevision.required_by?.some(
          (req) => req.purpose?.toLowerCase() === "approve" && req.is_open
        );
        if (hasPendingApprovals) {
          current_status = "Submitted For Approval";
        }
      }

      // Debug: Uncomment to see what data is available
      // console.log('Based On Version Content:', { rev, current_status, procedure_number, versionContent });

      // Only show if we have at least the revision number
      if (!rev && !procedure_number) {
        return null;
      }

      const rebase = currentRevision.based_on_version_details.version_content.rebase_status;
      return (
        <ShowLightText
          text={
            <>
              {rebase ? "Rebased" : "Based"} on {procedure_number} Rev {rev}, When {current_status}, at{" "}
              {convertUtcToFormattedTime(rebase ? currentRevision.based_on_version_details.created_at : currentRevision.created_at)}
            </>
          }
        />
      );
    }

    return null;
  }, [currentRevision, revisionData]);

  return basedOnText;
};

export default useRevisionBasedOnText;
